// Copyright (c) 2021-2022 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * The main module of Viewer defining major API classes.
 * Class {@link OccViewer.OccViewer} is the main entry point
 * to WebAssembly module setup and to Viewer initialization.
 *
 * @example
 * ```javascript
 * // Create viewer module object,
 * // where './viewer/' - path to viewer package folder over http.
 * const OccViewerModule = new OccViewer('./viewer/');
 * // Setup log verbose level.
 * // all messages (for development)
 * OccViewerModule.traceLevel = Message_Gravity.Message_Trace;
 *
 * // Check if browser supports WASM.
 * if (!OccViewerModule.isWasmSupported())
 * {
 *   var anElement = document.getElementById('output');
 *   anElement.innerHTML += "Browser is too old - WebAssembly support is missing!<br>";
 * }
 *
 * // Async viewer initialization function.
 * OccViewerModule.init()
 * .then(() =>
 * {
 *   // Some business logic
 * }
 * ```
 * @module
 */
import { Camera, Message_Gravity, Subview } from "../../OccViewer.js";
/**
 * Base class for OccViewer Module object.
 */
export class OccViewer {
    /**
     * Check browser support.
     * @return {boolean} TRUE if WASM supported
     */
    isWasmSupported() {
        // static
        try {
            if (typeof WebAssembly === "object" && typeof WebAssembly.instantiate === "function") {
                const aDummyModule = new WebAssembly.Module(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00));
                if (aDummyModule instanceof WebAssembly.Module) {
                    return (new WebAssembly.Instance(aDummyModule) instanceof WebAssembly.Instance);
                }
            }
        }
        catch (theErr) {
        }
        return false;
    }
    /** Check if multithreading is allowed. */
    isAllowMultithreading() {
        return (window.Worker !== undefined && window.Atomics !== undefined
            && window.SharedArrayBuffer !== undefined);
    }
    /** Return module by name. */
    getModule(moduleName) { throw new Error("Not implemented"); }
    /** Check if WASM module has been loaded. */
    get isWasmLoaded() { return this._myIsWasmLoaded; }
    /** Check if pthread version of viewer is loaded. */
    get isPthreadLoaded() { return this._myIsPthreadLoaded; }
    /** Init OccViewer Module and load WASM file. */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    let loadModule = (aModuleName) => __awaiter(this, void 0, void 0, function* () {
                        let importPthreadModule = () => __awaiter(this, void 0, void 0, function* () {
                            this._myBasePrefix = this._myBaseFolder + "wasm32-pthread/";
                            this.mainScriptUrlOrBlob = "./" + aModuleName;
                            let aRet = yield fetch(this._myBasePrefix + aModuleName);
                            let aSrc = yield aRet.text();
                            if (!aRet.ok) {
                                throw new Error(`Fail to fetch  ${this.mainScriptUrlOrBlob} Response finished with ${aRet.status}`);
                            }
                            aSrc += "\nexport default createOccViewer";
                            const aBlob = new Blob([aSrc], { type: "text/javascript" });
                            let AMod = yield import(
                            /* webpackIgnore: true */ URL.createObjectURL(aBlob));
                            this._myIsPthreadLoaded = true;
                            this._myIsWasmLoaded = true;
                            return AMod;
                        });
                        let importModule = () => __awaiter(this, void 0, void 0, function* () {
                            this._myBasePrefix = this._myBaseFolder + "wasm32/";
                            let ret = yield import(
                            /* webpackIgnore: true */ this._myBasePrefix + aModuleName);
                            this._myIsPthreadLoaded = false;
                            this._myIsWasmLoaded = true;
                            return ret;
                        });
                        if (this.isAllowMultithreading()) {
                            let aBackProps = null;
                            try {
                                let aMod = yield importPthreadModule();
                                aBackProps = {};
                                for (var aPropIter in this) {
                                    aBackProps[aPropIter] = this[aPropIter];
                                }
                                yield aMod.default(this);
                                return;
                            }
                            catch (err) {
                                console.log("Fail to load viewer in multi threading mode with error: ", err);
                                console.log("Switch to load in single threading mode");
                                // remove partially initialized fields
                                if (aBackProps != null) {
                                    for (var aPropIter in this) {
                                        if (aBackProps[aPropIter] === undefined) {
                                            delete this[aPropIter];
                                        }
                                    }
                                }
                            }
                        }
                        let aMod = yield importModule();
                        yield aMod.default(this);
                    });
                    yield loadModule("OccViewer-wasm32.js");
                    resolve(null);
                }
                catch (err) {
                    reject(err);
                }
            }));
            // init controllers
            this._myPropertyManager = this._cppPropertyManager();
            this._myRenderingParams = this._cppRenderingParams();
            this._myClippingPlanesController = this._cppClippingPlanesController();
            this._myExplodeViewController = this._cppExplodeViewController();
            this._myInteractiveContext = this._cppInteractiveContext();
            this._myGroundController = this._cppGroundController();
            this._myPmiController = this._cppPmiController();
            this._myModelLoader = this._cppModelLoader();
            this._myLightsController = this._cppLightsController();
            this._myAnimationManager = this._cppAnimationManager();
            this._myCollisionManager = this._cppCollisionManager();
            this._mySelectionManager = this._cppSelectionManager();
            this._myViewManager = this._cppViewManager();
            this._myInputBindings = this._cppInputBindings();
            // init focus camera Aspect_ScrollDelta
            this._myFocusCamera = new Camera(this, -1);
            // init views
            this._myRootView = new Subview();
            this._myRootView.wrap(this, 0);
            this._myFocusView = new Subview();
            this._myFocusView.wrap(this, -1);
            // call internal on init callbacks
            for (let aFunc of OccViewer._myInitFuncs) {
                aFunc(this);
            }
        });
    }
    /**
     * Main constructor.
     * @param {string} theBaseDataPath path to folder that contains wasm and wasm32 folders with sources
     */
    constructor(theBaseDataPath) {
        this._myBasePrefix = "";
        this._myOffset = [0, 0];
        this.mainScriptUrlOrBlob = "";
        this._myWillBeUpdated = false;
        this._myTraceLevel = Message_Gravity.Message_Trace.valueOf();
        // prefix for *.data file location
        this._myBaseFolder = theBaseDataPath;
        this._myIsWasmLoaded = false;
        this._myIsPthreadLoaded = false;
        // define WebGL canvas for WebAssembly viewer
        this._myCanvasId = "occViewerCanvas";
        this.canvas = document.getElementById(this._myCanvasId);
        this.canvas.tabIndex = -1;
        this.canvas.onclick = (theEvent) => {
            this.canvas.focus();
        };
        // tell Emscripten and Draw Harness to not use std::cin for commands input
        this.noExitRuntime = true;
        // bind WebAssembly callbacks to this context
        this.print = this.print.bind(this);
        this.printErr = this.printErr.bind(this);
        this.printMessage = this.printMessage.bind(this);
        this.locateFile = this.locateFile.bind(this);
        this._myCallbacks = {};
    }
    //#region Module interface
    /**
     * C++ `std::cout` callback redirecting to Terminal.
     * This callback is almost never called, since OCCT relies on
     * `Message_Messenger` interface redirecting to [[OccViewer.printMessage]].
     * @param {string} theText text to print
     */
    print(theText) { this.printMessage(theText, Message_Gravity.Message_Info); }
    /**
     * C++ `std::cerr` callback redirecting to Terminal.
     * This callback is almost never called, since OCCT relies on
     * `Message_Messenger` interface redirecting to [[OccViewer.printMessage]].
     * @param {string} theText text to print
     */
    printErr(theText) { this.printMessage(theText, Message_Gravity.Message_Fail); }
    /**
     * C++ `Message::Send()` callback redirecting to Terminal.
     * @param {string} theText text to print
     * @param {number} theGravity message gravity within 0..4 range
     */
    printMessage(theText, theGravity) {
        if (theGravity.valueOf() < this._myTraceLevel) {
            return;
        }
        switch (theGravity) {
            case Message_Gravity.Message_Trace: // trace
                console.debug(theText);
                return;
            case Message_Gravity.Message_Info: // info
                console.info(theText);
                return;
            case Message_Gravity.Message_Warning: // warning
                console.warn(theText);
                return;
            case Message_Gravity.Message_Alarm: // alarm
            case Message_Gravity.Message_Fail: // fail
                console.error(theText);
                return;
        }
        console.warn(theText);
    }
    /**
     * Callback returning file path for loading WebAssembly components.
     * @param {string} thePath file path to locate
     * @param {string} thePrefix default file prefix
     * @return {string} full path to the resource
     */
    locateFile(thePath, thePrefix) {
        return this._myBasePrefix + thePath;
    }
    /**
     * Get gravity level for logging messages.
     * Consider increasing the level within production application environment to log only critical errors.
     * @return {Message_Gravity} lower message gravity to log
     */
    get traceLevel() { return this._myTraceLevel; }
    /**
     * Set gravity level for logging messages.
     * @param {Message_Gravity} theGravity lower message gravity to log
     */
    set traceLevel(theGravity) {
        // Messages are filtered by both OccViewer.printMessage() and Message_Printer::send().
        // Propagating filter level to Message_Printer might improve performance by skipping extra function calls for too verbose messages.
        this._myTraceLevel = theGravity.valueOf();
        if (this._myIsWasmLoaded) {
            this._cppSetTraceLevel(theGravity.valueOf());
        }
    }
    /**
     * Register font (file in TTF format).
     * This should be done before displaying text labels using this font,
     * otherwise a fallback font will be used instead.
     * @param {string} theFontUrl url of font to download and register
     * @param {string} theFontName font name or empty string to keep name from file itself
     * @param {boolean} theToOverride override existing font family
     */
    registerFontFromUrl(theFontURl_1) {
        return __awaiter(this, arguments, void 0, function* (theFontURl, theFontName = "", theToOverride = false) {
            const aPaths = theFontURl.split("/");
            const aFileName = "/fonts/" + aPaths.pop();
            yield this.modelLoader.uploadUrl(theFontURl, aFileName, false);
            this._cppRegisterFont(aFileName, theFontName, theToOverride);
        });
    }
    /** Update View. */
    updateView() {
        if (this._myWillBeUpdated) {
            return;
        }
        // set timer to avoid multiple calls
        this._myWillBeUpdated = true;
        setTimeout(() => {
            this._myWillBeUpdated = false;
            this._cppUpdateView();
        }, 0);
    }
    /**
     * Pick 3d point by 2d coordinates and store.
     * @param {number}  theX x coordinate of canvas
     * @param {number}  theY y coordinate of canvas
     * @param {boolean} theToStickToPickRay when TRUE, the result point will lie on picking ray
     * @param {Subview} theView view to pick in (null for main view)
     * @return 3d coordinate or null in case if nothing picked
     */
    pickPoint(theX, theY, theToStickToPickRay, theView = null) {
        const theViewId = theView == null ? -1 : theView.id;
        if (this._cppPickPoint(theX, theY, theToStickToPickRay, theViewId)) {
            return this._cppGetLastPickedPoint();
        }
        else {
            return null;
        }
    }
    /**
     * Make screenshot image.
     * @param {theWidth} theWidth of screenshot (-1 - take size of canvas)
     * @param {theHeight} theHeight of screenshot  (-1 - take size of canvas)
     * @param {theWidth} theViewId view ID (-1 for active view)
     * @param {theIsGrayscale} theIsGrayscale final image will be in grayscale (false as default)
     * @param {theToBase64} theToBase64 return image as base64 string (false as default)
     * @return Uint8Array or base64 string of image or null if failed
     */
    makeScreenshotImage(theWidth, theHeight, theViewId = -1, theIsGrayscale = false, theToBase64 = false) {
        const aWidth = theWidth == -1 ? this.canvas.width : theWidth;
        const aHeight = theHeight == -1 ? this.canvas.height : theHeight;
        const aBuffer = this._cppMakeScreenshot(theViewId, aWidth, aHeight, theIsGrayscale, theToBase64);
        if (!aBuffer.size) {
            return null;
        }
        if (theToBase64) {
            //@ts-ignore
            const aString = this.UTF8ToString(aBuffer.pointer, aBuffer.size);
            this._free(aBuffer.pointer);
            return aString;
        }
        else {
            const anArray = new Uint8Array(this.HEAPU8.subarray(aBuffer.pointer, aBuffer.pointer + aBuffer.size));
            this._free(aBuffer.pointer);
            return anArray;
        }
    }
    /**
     * Download file.
     * @param theFilename - name of file
     * @param theBuffer - buffer to download
     * @param theType  - MIME type of file
     */
    downloadFile(theFilename, theBuffer, theType = "application/octet-stream") {
        const aBlob = new Blob([theBuffer], { type: theType });
        const aUrl = URL.createObjectURL(aBlob);
        const aLink = document.createElement("a");
        aLink.href = aUrl;
        aLink.download = theFilename;
        aLink.click();
    }
    /**
     * Make screenshot and download.
     * @param {theFilename} theFilename of screenshot which will be downloaded
     * @param {theWidth} theWidth of screenshot (-1 - take size of canvas)
     * @param {theHeight} theHeight of screenshot  (-1 - take size of canvas)
     * @param {theWidth} theViewId view ID (-1 for active view)
     * @param {theIsGrayscale} theIsGrayscale final image will be in grayscale (false as default)
     * @return true if success
     */
    makeScreenshot(theFilename, theWidth, theHeight, theViewId = -1, theIsGrayscale = false) {
        const aWidth = theWidth == -1 ? this.canvas.width : theWidth;
        const aHeight = theHeight == -1 ? this.canvas.height : theHeight;
        const imageData = this.makeScreenshotImage(aWidth, aHeight, theViewId, theIsGrayscale);
        if (imageData == null) {
            return false;
        }
        this.downloadFile(theFilename, imageData, "image/png");
        return true;
    }
    /** Update Offset of canvas relative to window. */
    updateCanvasOffset() {
        const aRect = this.canvas.getBoundingClientRect();
        const anOffsetX = Math.round(aRect.left);
        const anOffsetY = Math.round(aRect.top);
        if (this._myOffset[0] == anOffsetX && this._myOffset[1] == anOffsetY) {
            return;
        }
        this._myOffset[0] = anOffsetX;
        this._myOffset[1] = anOffsetY;
        this._cppSetCanvasOffset(anOffsetX, anOffsetY);
    }
    /** Resize rendering window. */
    resize() { this._cppResize(); }
    /**
     * Get View Cube controller - an interface for managing properties of auxiliary View Cube interactive object displayed at View corner.
     * @return {ViewCube} view cube controller
     */
    get viewCube() { return this._cppViewCubeController(); }
    /**
     * Get Slicer controller - an interface for managing creating slices.
     * @return {Slicer} view cube controller
     */
    get slicer() { return this._cppSlicerController(); }
    /**
     * Get camera controller - an interface for managing various camera view properties.
     * @return {Camera} camera controller
     */
    get camera() { return this._myFocusCamera; }
    /**
     * Get root view object (either main view itself or composer of subviews).
     * @return {Subview} View object
     */
    get rootView() { return this._myRootView; }
    /**
     * Get view object receiving focus.
     * @return {Subview} View object
     */
    get focusView() { return this._myFocusView; }
    /**
     * Get clipping plane controller - a global clipping plane object that could be applied to entire view or specific objects.
     * Global clipping plane object is always defined, but disabled by default.
     * @return {ClippingPlane} clipping plane controller.
     */
    get clippingPlane() { return this._myClippingPlanesController; }
    /**
     * Get explode view controller - a global explode view object that could be applied to entire view or specific objects.
     * Global explode view object is always defined, but disabled by default.
     * @return {ExplodeViewController} explode view controller.
     */
    get explodeView() { return this._myExplodeViewController; }
    /**
     * Get model loader - an interface for loading models from various file formats.
     * See also [[InteractiveMesh]] for object-oriented interface to this functionality.
     * @return {ModelLoader} model loader.
     */
    get modelLoader() { return this._myModelLoader; }
    /**
     * Get subview manager.
     * @return {ViewManager} subview manager object
     */
    get viewManager() { return this._myViewManager; }
    /**
     * Get input bindings
     * @return {InputBindings} input bindings object
     */
    get inputBindings() { return this._myInputBindings; }
    /**
     * Get renderingParams controller - an interface for querying and changing various rendering properties.
     * @return {RenderingParams} rendering controller
     */
    get renderingParams() { return this._myRenderingParams; }
    /**
     * Get Interactive Context - a low-level manager of interactive objects in the Viewer.
     * See also [[InteractiveObject]] and it's subclasses for object-oriented interface to this functionality.
     * @return {InteractiveContext} interactive context.
     */
    get interactiveContext() { return this._myInteractiveContext; }
    /**
     * Get ground controller - a manager of a global ground presentation object (hidden by default).
     * @return {Ground} ground controller.
     */
    get ground() { return this._myGroundController; }
    /**
     * Get PMI controller - a low-level manager of PMI interactive objects.
     * See also [[TextLabel]] and [[LengthDimension]] for object-oriented interface to PMI functionality.
     * @return {PmiController} PMI controller.
     */
    get pmi() { return this._myPmiController; }
    /**
     * Get lights controller - an interface for managing list of active light sources and other lighting properties.
     * @return {LightsController} lights controller.
     */
    get lights() { return this._myLightsController; }
    /**
     * Get animation manager.
     * @return {AnimationManager} animation manager
     */
    get animationManager() { return this._myAnimationManager; }
    /**
     * Get collision manager.
     * @return {CollisionManager} collision manager
     */
    get collisionManager() { return this._myCollisionManager; }
    /**
     * Get interactive point controller
     * @return {InteractivePointController} interactive point controller
     */
    get interactivePointController() {
        return this._cppInteractivePointController();
    }
    /**
     * Get interactive line controller
     * @return {InteractiveLineController} interactive line controller
     */
    get interactiveLineController() {
        return this._cppInteractiveLineController();
    }
    /**
   * Get interactive contour controller
   * @return {InteractiveContourController} interactive contour controller
   */
    get interactiveContourController() {
        return this._cppInteractiveContourController();
    }
    /**
     * Get interactive plane controller - an interface responsible for managing interactive planes.
     * @return {InteractivePlaneController} interactive plane controller
     */
    get interactivePlaneController() {
        // @ts-ignore
        return this._cppInteractivePlaneController();
    }
    /**
     * Get selection manager - an interface for managing the list of selected objects and various selection properties.
     * @return {SelectionManager} selection manager.
     */
    get selectionManager() { return this._mySelectionManager; }
    /**
     * Get event manager - an interface for subscribing and unsubscribing to various callbacks (like selection changes).
     * @return {EventManager} selection manager.
     */
    get eventManager() { return this._myEventManger; }
    /**
     * Get Property manager - an interface for accessing properties of model objects.
     * See also [[InteractiveMesh]] for object-oriented interface to this functionality.
     * @return {PropertyManager} property manager object
     */
    get propertyManager() { return this._myPropertyManager; }
    /** Gets multi threading is on; requires WASM module built with pthread support ([[OccViewer.isAllowMultithreading]] and [[OccViewer.isPthreadLoaded]]. */
    get isMultithreadingMode() { return this._cppIsMultithreadingMode(); }
    /** Enable/disable multi threading; has no effect if loaded WASM module was built without pthread support ([[OccViewer.isAllowMultithreading]] and [[OccViewer.isPthreadLoaded]]).*/
    set isMultithreadingMode(theIsOn) { this._cppSetMultithreadingMode(theIsOn); }
    /** Returns manipulator object - an auxiliary presentation object for interactive movements of presentations in the Viewer. */
    get manipulator() { return this._cppManipulator(); }
    //@internal
    static onInit(aFunc) { OccViewer._myInitFuncs.push(aFunc); }
    /**
     * Memory allocation within C++ heap (forward declaration).
     * @param {number} theSize number of bytes to allocation
     * @return {Uint8Array} offset within the heap
     */
    _malloc(theSize) { throw new Error("Method not implemented"); }
    /**
     * Memory deallocation within C++ heap (forward declaration).
     * @param {number} theOffset offset within the heap
     */
    _free(theOffset) { throw new Error("Method not implemented"); }
    /**
     * Set gravity level for C++ OCCT messenger.
     * @param {number} theGravity lower message gravity to log
     */
    _cppSetTraceLevel(theGravity) { throw new Error("Method not implemented"); }
    /**
     * Register font.
     * @param {string} theFontPath the font path
     * @param {string} theFontName font name or empty string to keep name from file itself
     * @param {boolean} theToOverride override existing font family
     */
    _cppRegisterFont(theFontPath, theFontName, theToOverride) {
        throw new Error("Method not implemented");
    }
    /**
     * Get view cube controller.
     * @return {ViewCube} view cube controller
     */
    _cppViewCubeController() { throw new Error("Method not implemented"); }
    /**
     * Get slicer controller.
     * @return {Slicer} view cube controller
     */
    _cppSlicerController() { throw new Error("Method not implemented"); }
    /**
     * Get selection manager.
     * @return {SelectionManager} selection manager.
     */
    _cppSelectionManager() { throw new Error("Method not implemented"); }
    /**
     * Get clipping plane controller.
     * @return {ClippingPlane} clipping plane controller.
     */
    _cppClippingPlanesController() {
        throw new Error("Method not implemented");
    }
    /**
     * Get explode view controller.
     * @return {ExplodeViewController} explode view controller.
     */
    _cppExplodeViewController() {
        throw new Error("Method not implemented");
    }
    /**
     * Get model loader.
     * @return {ModelLoader} model loader
     */
    _cppModelLoader() { throw new Error("Method not implemented"); }
    /**
     * Get view manager.
     * @return {ViewManager} view manager
     */
    _cppViewManager() { throw new Error("Method not implemented"); }
    /**
     * Get input bindings.
     * @return {InputBindings} input bindings
     */
    _cppInputBindings() { throw new Error("Method not implemented"); }
    /**
     * Get property manager
     * @return {Property} property manager object
     */
    _cppPropertyManager() { throw new Error("Method not implemented"); }
    /**
     * Get lights controller
     * @return {LightsController} lights controller
     */
    _cppLightsController() { throw new Error("Method not implemented"); }
    /**
     * Get animation manager.
     * @return {AnimationManager} animation manager
     */
    _cppAnimationManager() { throw new Error("Method not implemented"); }
    /**
     * Get collision manager.
     * @return {CollisionManager} collision manager
     */
    _cppCollisionManager() { throw new Error("Method not implemented"); }
    /**
     * Get interactive point controller.
     * @return {InteractivePointController} interactive point controller
     */
    _cppInteractivePointController() {
        throw new Error("Method not implemented");
    }
    /**
     * Get interactive line controller.
     * @return {InteractiveLineController} interactive line controller
     */
    _cppInteractiveLineController() {
        throw new Error("Method not implemented");
    }
    /**
     * Get interactive contour controller.
     * @return {InteractiveContourController} interactive contour controller
     */
    _cppInteractiveContourController() {
        throw new Error("Method not implemented");
    }
    /**
     * Get interactive context
     * @return {InteractiveContext} interactive context object
     */
    _cppInteractiveContext() {
        throw new Error("Method not implemented");
    }
    /**
     * Get PMI controller.
     * @return {PmiController} PMI controller object.
     */
    _cppPmiController() { throw new Error("Method not implemented"); }
    /**
     * Get ground controller
     * @return {Ground} ground controller object.
     */
    _cppGroundController() { throw new Error("Method not implemented"); }
    /**
     * Get rendering controller.
     * @return {RenderingParams} rendering controller object.
     */
    _cppRenderingParams() { throw new Error("Method not implemented"); }
    /** Gets multi threading is on. */
    _cppIsMultithreadingMode() { throw new Error("Method not implemented"); }
    /**
     * Enable multi threading.
     * @param {boolean} theIsOn enable if true
     */
    _cppSetMultithreadingMode(theIsOn) {
        throw new Error("Method not implemented");
    }
    /**
     * Make screenshot.
     * @param {number} theViewID view ID
     * @param {number} theWidth width of image
     * @param {number} theHeight height of image
     * @param {number} theBuffer preallocated buffer
     * @return {WasmBuffer} wasmBuffer
     */
    _cppMakeScreenshot(theViewID, theWidth, theHeight, theIsGrayscale, theToBase64) {
        throw new Error("Method not implemented");
    }
    /** Set Offset of canvas relative to window. */
    _cppSetCanvasOffset(theOffsetX, theOffsetY) {
        throw new Error("Method not implemented");
    }
    /** Resize window. */
    _cppResize() { throw new Error("Method not implemented"); }
    /**
     * Call callback by IPromise pointer.
     * @param {number} theCallback pointer to IPromise object
     */
    _cppWasmResolve(theCallback) { throw new Error("Method not implemented"); }
    /**
     * Call on error callback by IPromise pointer.
     * @param {number} theCallback pointer to IPromise object
     * @param {string} theErr error message
     */
    _cppWasmReject(theCallback, theErr) {
        throw new Error("Method not implemented");
    }
    /** Update View. */
    _cppUpdateView() { throw new Error("Method not implemented"); }
    /** Returns manipulator object. */
    _cppManipulator() { throw new Error("Method not implemented"); }
    /**
     * Subscribe callback to event. If event doesn't exists it will create new event.
     * @param {string} theEventName event name
     * @param {IObservableCallback} theCallback callback
     */
    _cppSubscribe(theEventName, theCallback) {
        throw new Error("Method not implemented");
    }
    /**
     * Unsubscribe callback from event.
     * @param {string} theEventName event name
     * @param {IObservableCallback} theCallback callback
     */
    _cppUnsubscribe(theEventName, theCallback) {
        throw new Error("Method not implemented");
    }
    /**
     * Fire callbacks of event.
     * @param {string} theEventName event name
     * @param {IObservableCallback}  theCallback
     * @return {number} the status of emit
     */
    _cppEmit(theEventName, theContext) {
        throw new Error("Method not implemented");
    }
    /**
     * Return the event by its name. If event doesn't exists it will create new one.
     * @param {string} theEventName event name
     * @return {Observable} the event.
     */
    _cppGetEvent(theEventName) {
        throw new Error("Method not implemented");
    }
    /**
     * Add Event.
     * @param {string} theEventName event name
     * @param {Observable} the event controller to add.
     */
    _cppAddEvent(theEventName, theEvent) {
        throw new Error("Method not implemented");
    }
    /**
     * Get last mouse event info.
     * @return {MouseEventInfo} last mouse event info
     */
    _cppGetLastMouseEventInfo() {
        throw new Error("Method not implemented");
    }
    /**
     * Get last picked point by _cppPickPoint command
     * @return lastPickedPoint by _cppPickPoint command
     */
    _cppGetLastPickedPoint() { throw new Error("Method not implemented"); }
    /**
     * Pick 3d point by 2d coordinates and store.
     * to access to the picked point please call _cppGetLastPickedPoint;
     * @param {number}  theX x coordinate of canvas
     * @param {number}  theY y coordinate of canvas
     * @param {boolean} theToStickToPickRay when TRUE, the result point will lie on picking ray
     * @param {number} theViewId view ID
     * @return {boolean} status of pick point
     */
    _cppPickPoint(theX, theY, theToStickToPickRay, theViewId) {
        throw new Error("Method not implemented");
    }
}
/**@internal*/
OccViewer._myInitFuncs = [];
